import { useCallback, useMemo, useState } from 'react';

import { htmlToPlaintext } from '@lyearn/lygo-blocks';
import { GenerativeTaskContextProps } from '@/components/LyearnAI/types';
import {
  CreateGenerativeTaskInput,
  GenerativeTaskTypeEnum,
  TextTransformationEnum,
  TransformTextInput,
} from '@/types/schema';

import { AIBaseEditorProps, AIEditorContextProps, AIEditorState } from '../types';

function getEditorState(editorContainsText: boolean, showSuggestions: boolean): AIEditorState {
  if (showSuggestions) {
    return editorContainsText ? AIEditorState.DraftCompletion : AIEditorState.EmptyCompletion;
  } else {
    return editorContainsText ? AIEditorState.DraftInput : AIEditorState.Empty;
  }
}

export default function useAIEditorContextValue(
  props: Pick<AIBaseEditorProps, 'textUseCase' | 'topic' | 'onTopicError' | 'value'>,
  generativeTaskContext: GenerativeTaskContextProps,
): AIEditorContextProps {
  const { textUseCase, topic: topicHtml, onTopicError, value } = props;
  const { generativeTask, clearGenerativeTask, createGenerativeTask, isTyping } =
    generativeTaskContext;

  const suggestedText = generativeTask?.result.transformText?.text ?? null;

  const editorContainsText = useMemo(() => {
    return Boolean(htmlToPlaintext(value ?? ''));
  }, [value]);

  const showSuggestions = Boolean(suggestedText) || isTyping;

  const editorState = getEditorState(editorContainsText, showSuggestions);

  const [prevGenerativeTaskInput, setPrevGenerativeTaskInput] =
    useState<CreateGenerativeTaskInput | null>(null);
  const [lastTransformation, setLastTransformation] = useState<TextTransformationEnum | null>(null);

  const isAIActive = [AIEditorState.EmptyCompletion, AIEditorState.DraftCompletion].includes(
    editorState,
  );

  const showEditor = [
    AIEditorState.Empty,
    AIEditorState.DraftInput,
    AIEditorState.DraftCompletion,
  ].includes(editorState);

  const doTransformText = useCallback(
    (transformTextInput: Pick<TransformTextInput, 'text' | 'transformation' | 'tone'>) => {
      const generativeTaskInput: CreateGenerativeTaskInput = {
        type: GenerativeTaskTypeEnum.TransformText,
        input: {
          transformText: {
            ...transformTextInput,
            useCase: textUseCase,
          },
        },
      };
      setPrevGenerativeTaskInput(generativeTaskInput);
      setLastTransformation(transformTextInput.transformation);
      clearGenerativeTask();
      createGenerativeTask(generativeTaskInput);
    },
    [clearGenerativeTask, createGenerativeTask, textUseCase],
  );

  const doTryAgain = useCallback(() => {
    if (!prevGenerativeTaskInput) return;
    clearGenerativeTask();
    createGenerativeTask(prevGenerativeTaskInput);
  }, [clearGenerativeTask, createGenerativeTask, prevGenerativeTaskInput]);

  // in some use cases, we use editor value as topic,
  // so we need to parse it to plain text, to check if it is empty
  const topic = useMemo(() => htmlToPlaintext(topicHtml ?? ''), [topicHtml]);

  const suggestionBodyHighlighted = editorState === AIEditorState.EmptyCompletion && !isTyping;

  return {
    suggestedText,
    showSuggestions,
    topic,
    onTopicError,
    editorState,
    isAIActive,
    showEditor,
    textUseCase,
    lastTransformation,
    suggestionBodyHighlighted,
    doTransformText,
    doTryAgain,
  };
}
